import React from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';

const competitorColumns = [
  { key: 'zapier', label: 'Zapier', sub: 'iPaaS' },
  { key: 'unito', label: 'Unito', sub: '2-way sync' },
  { key: 'getint', label: 'Getint', sub: 'Managed sync' },
];

const featureData = [
  {
    feature: 'True 2-Way Sync',
    zapier: 'Paired flows',
    unito: 'Native',
    getint: 'Supported',
    datlynk: 'Default',
  },
  {
    feature: 'Setup Style',
    zapier: 'Builder',
    unito: 'No-code UI',
    getint: 'Assisted',
    datlynk: 'Guided wizard',
  },
  {
    feature: 'Setup Speed',
    zapier: 'Fast',
    unito: 'Quick',
    getint: 'Minutes',
    datlynk: '< 60 sec',
  },
  {
    feature: 'Structure Handling',
    zapier: 'Limited',
    unito: 'Mixed',
    getint: 'Supported',
    datlynk: 'Preserved',
  },
  {
    feature: 'Field Mapping',
    zapier: 'Custom',
    unito: 'Included',
    getint: 'Included',
    datlynk: 'Schema-aware',
  },
  {
    feature: 'Control Layer',
    zapier: 'Multi-step',
    unito: 'Rules + match',
    getint: 'Scripting',
    datlynk: 'One UI',
  },
  {
    feature: 'Sync Speed',
    zapier: 'Task-based',
    unito: '5-15 min',
    getint: '3 min+',
    datlynk: '15 min / <5 min',
  },
  {
    feature: 'Pricing',
    zapier: '$19.99+',
    unito: 'Sales-led',
    getint: '$1,800/yr+',
    datlynk: '$0 / $24+',
  },
];

const comparisonRows = featureData;

const Comparison = () => {
  const renderDesktopCell = (value, highlight = false) => {
    if (typeof value === 'string') {
      return (
        <Text fontSize={highlight ? 'sm' : '11px'} fontWeight={highlight ? '800' : '700'} color={highlight ? 'brand.primary' : 'text.muted'} lineHeight="1.35">
          {value}
        </Text>
      );
    }
    return value;
  };

  const renderMobileCell = (value, highlight = false) => {
    if (typeof value === 'string') {
      return (
        <Text fontSize={highlight ? '10px' : '9px'} fontWeight={highlight ? '800' : '600'} color={highlight ? 'brand.primary' : 'text.muted'} lineHeight="1.3">
          {value}
        </Text>
      );
    }
    return value;
  };

  return (
    <Box
      as="section"
      id="comparison"
      py={{ base: 24, md: 32 }}
      bg="landing.surface"
      position="relative"
      overflow="hidden"
    >
      <Box
        position="absolute"
        bottom="0"
        right="0"
        w="40%"
        h="40%"
        bg="radial-gradient(circle, rgba(37, 99, 235, 0.05) 0%, transparent 70%)"
        pointerEvents="none"
      />
      <Box
        position="absolute"
        top="10%"
        left="-5%"
        w="30%"
        h="50%"
        bg="linear-gradient(135deg, rgba(34, 211, 238, 0.06) 0%, transparent 80%)"
        pointerEvents="none"
      />

      <Box maxW="1200px" mx="auto" px={6} position="relative" zIndex={1}>
        <Box textAlign="center" mb={12}>
          <Flex
            display="inline-flex"
            alignItems="center"
            px={3}
            py={1.5}
            borderRadius="full"
            bg="brand.subtle"
            borderWidth="1px"
            borderColor="landing.border"
            mb={4}
            mx="auto"
          >
            <Text fontSize="xs" fontWeight="800" color="brand.primary" letterSpacing="0.05em">
              REPRESENTATIVE COMPARISON
            </Text>
          </Flex>
          <Text as="h2" fontSize={{ base: '3xl', md: '5xl' }} fontWeight="900" color="text.main" letterSpacing="-0.03em" mb={4}>
            Compare <Box as="span" color="brand.primary">Sync Approaches</Box>
          </Text>
          <Text fontSize="lg" color="text.muted" maxW="640px" mx="auto" lineHeight="1.7">
            Quick view of where DatLynk is stronger on sync, structure, setup, and pricing.
          </Text>
        </Box>

        <Box display={{ base: 'block', lg: 'none' }} mb={2}>
          <Box
            borderRadius="2xl"
            border="1px solid"
            borderColor="landing.border"
            boxShadow="xl"
            overflow="hidden"
            bg="landing.card"
          >
            <Flex align="center" justify="center" gap={2} py={2.5} bg="brand.subtle" borderBottom="1px solid" borderColor="brand.muted">
              <Text fontSize="10px" fontWeight="800" color="brand.primary" letterSpacing="0.06em">
                ← SWIPE TO COMPARE ALL TOOLS →
              </Text>
            </Flex>
            <Box overflowX="auto" style={{ WebkitOverflowScrolling: 'touch' }}>
              <Box style={{ minWidth: '560px' }}>
                <Flex py={3} px={0} borderBottom="1px solid" borderColor="landing.border" alignItems="center" bg="landing.surface">
                  <Box
                    w="110px"
                    flexShrink={0}
                    px={3}
                    position="sticky"
                    left={0}
                    bg="landing.surface"
                    zIndex={2}
                    borderRight="1px solid"
                    borderColor="landing.border"
                    alignSelf="stretch"
                    display="flex"
                    alignItems="center"
                  >
                    <Text fontSize="9px" fontWeight="800" color="text.muted" letterSpacing="0.06em" textTransform="uppercase">
                      Capability
                    </Text>
                  </Box>

                  {competitorColumns.map((column) => (
                    <Box key={column.key} w="90px" flexShrink={0} textAlign="center" px={2} borderRight="1px solid" borderColor="landing.border">
                      <Text fontSize="9px" fontWeight="800" color="text.main" textTransform="uppercase">
                        {column.label}
                      </Text>
                      <Text fontSize="8px" fontWeight="500" color="text.muted" mt={0.5}>
                        {column.sub}
                      </Text>
                    </Box>
                  ))}

                  <Box w="100px" flexShrink={0} textAlign="center" px={2} bg="brand.subtle" alignSelf="stretch" display="flex" flexDir="column" alignItems="center" justifyContent="center">
                    <Text fontSize="10px" fontWeight="900" color="brand.primary" textTransform="uppercase">
                      DatLynk
                    </Text>
                    <Text fontSize="8px" fontWeight="700" color="brand.primary" opacity={0.7} mt={0.5}>
                      True 2-way sync
                    </Text>
                  </Box>
                </Flex>

                {comparisonRows.map((row, index) => (
                  <Flex
                    key={index}
                    alignItems="center"
                    borderBottom={index < comparisonRows.length - 1 ? '1px solid' : 'none'}
                    borderColor="landing.border"
                  >
                    <Box
                      w="110px"
                      flexShrink={0}
                      px={3}
                      py={3.5}
                      position="sticky"
                      left={0}
                      bg="landing.card"
                      zIndex={2}
                      borderRight="1px solid"
                      borderColor="landing.border"
                      alignSelf="stretch"
                      display="flex"
                      alignItems="center"
                    >
                      <Text fontSize="10px" fontWeight="700" color="text.main" lineHeight="1.3">
                        {row.feature}
                      </Text>
                    </Box>

                    {competitorColumns.map((column) => (
                      <Box key={column.key} w="90px" flexShrink={0} display="flex" justifyContent="center" alignItems="center" textAlign="center" px={2} py={3.5} borderRight="1px solid" borderColor="landing.border">
                        {renderMobileCell(row[column.key])}
                      </Box>
                    ))}

                    <Box w="100px" flexShrink={0} display="flex" justifyContent="center" alignItems="center" textAlign="center" px={2} py={3.5} bg="brand.subtle" alignSelf="stretch">
                      {renderMobileCell(row.datlynk, true)}
                    </Box>
                  </Flex>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>

        <Box display={{ base: 'none', lg: 'block' }} borderRadius="2xl" border="1px solid" borderColor="landing.border" boxShadow="xl" overflow="hidden" bg="landing.card" overflowX="auto">
          <Box minW="900px">
            <Flex py={6} px={4} borderBottom="1px solid" borderColor="landing.border" alignItems="center" bg="landing.surface">
              <Text flex={2} fontSize="xs" fontWeight="800" color="text.muted" textTransform="uppercase">
                Capability
              </Text>

              {competitorColumns.map((column, index) => (
                <Box key={column.key} flex={1.2} textAlign="center" px={2} borderLeft={index === 0 ? 'none' : '1px solid'} borderColor="landing.border">
                  <Text fontSize="xs" fontWeight="800" color="text.main" textTransform="uppercase">
                    {column.label}
                  </Text>
                  <Text fontSize="10px" color="text.muted" mt={1} fontWeight="500">
                    {column.sub}
                  </Text>
                </Box>
              ))}

              <Box flex={1.5} bg="brand.subtle" py={6} mt={-6} mb={-6} alignSelf="stretch" display="flex" flexDir="column" alignItems="center" justifyContent="center" mx={-1} borderLeft="1px solid" borderRight="1px solid" borderColor="brand.muted">
                <Text fontSize="sm" fontWeight="900" color="brand.primary" textTransform="uppercase">
                  DatLynk
                </Text>
                <Text fontSize="10px" color="brand.primary" mt={1} fontWeight="700" opacity={0.8}>
                  True 2-way sync
                </Text>
              </Box>
            </Flex>

            <Box>
              {comparisonRows.map((row, index) => (
                <Flex key={index} py={5} px={4} borderBottom={index < comparisonRows.length - 1 ? '1px solid' : 'none'} borderColor="landing.border" alignItems="center">
                  <Text flex={2} fontWeight="700" color="text.main" fontSize="sm">
                    {row.feature}
                  </Text>

                  {competitorColumns.map((column, cellIndex) => (
                    <Box key={column.key} flex={1.2} display="flex" justifyContent="center" textAlign="center" px={2} borderLeft={cellIndex === 0 ? 'none' : '1px solid'} borderColor="landing.border">
                      {renderDesktopCell(row[column.key])}
                    </Box>
                  ))}

                  <Box flex={1.5} display="flex" justifyContent="center" textAlign="center" bg="brand.subtle" py={5} mt={-5} mb={-5} alignSelf="stretch" alignItems="center" mx={-1} borderLeft="1px solid" borderRight="1px solid" borderColor="brand.muted">
                    {renderDesktopCell(row.datlynk, true)}
                  </Box>
                </Flex>
              ))}
            </Box>
          </Box>
        </Box>

        <Text fontSize="xs" color="text.muted" mt={4} textAlign="center">
          Comparison rows summarize public product pages and current DatLynk pricing as checked in April 2026.
        </Text>
      </Box>
    </Box>
  );
};

export default Comparison;
